import { ResponseError } from '@nomiss/sdk';

export async function unwrapApiData<T>(raw: unknown): Promise<T> {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

/**
 * True when an API failure is an authentication failure.
 * Duck-typed so a duplicated `ApiRequestError` class (separate bundle copies)
 * still counts.
 */
export function isUnauthorizedApiError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const record = error as { status?: unknown; response?: { status?: unknown } };
  if (record.status === 401) return true;
  return record.response?.status === 401;
}

export class ApiRequestError extends Error {
  /**
   * Stable machine-readable code from the API envelope, when present. Prefer
   * branching on this over matching `message`, which is not a contract.
   */
  readonly errorCode?: string | null;

  constructor(
    readonly status: number,
    message: string,
    readonly name = 'ApiRequestError',
    errorCode?: string,
  ) {
    super(message);
    this.name = name;
    if (errorCode) {
      this.errorCode = errorCode;
    }
  }
}

export async function parseApiErrorResponse(
  response: Response,
  fallback: string,
  ErrorClass: typeof ApiRequestError = ApiRequestError,
): Promise<ApiRequestError> {
  const json = (await response.json().catch(() => ({}))) as {
    message?: string | string[];
    errorCode?: string;
  };

  const rawMessage = json.message;
  const joinedMessage = Array.isArray(rawMessage)
    ? rawMessage.join(', ')
    : typeof rawMessage === 'string'
      ? rawMessage
      : '';
  const message =
    joinedMessage &&
    !/^[A-Z][A-Za-z]+(?: [A-Z][A-Za-z]+)* Exception$/.test(joinedMessage)
      ? joinedMessage
      : fallback;

  return new ErrorClass(
    response.status,
    message || fallback,
    ErrorClass.name,
    typeof json.errorCode === 'string' ? json.errorCode : undefined,
  );
}

async function handleSdkResponse<T>(
  response: Response,
  fallback: string,
  ErrorClass: typeof ApiRequestError,
): Promise<T> {
  if (!response.ok) {
    throw await parseApiErrorResponse(response, fallback, ErrorClass);
  }

  const text = await response.text();
  if (!text.trim()) {
    return undefined as T;
  }

  try {
    const json = JSON.parse(text) as unknown;
    return unwrapApiData<T>(json);
  } catch {
    return undefined as T;
  }
}

export async function executeSdkRawRequest<T>(options: {
  request: () => Promise<{ raw: Response }>;
  fallback: string;
  ErrorClass?: typeof ApiRequestError;
}): Promise<T> {
  const ErrorClass = options.ErrorClass ?? ApiRequestError;

  try {
    const raw = await options.request();
    return handleSdkResponse<T>(raw.raw, options.fallback, ErrorClass);
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }

    if (error instanceof ResponseError) {
      throw await parseApiErrorResponse(
        error.response,
        options.fallback,
        ErrorClass,
      );
    }

    throw error;
  }
}

/** For SDK DTOs that model the full API envelope (`status`, `message`, `data`). */
export async function executeSdkEnvelopeRequest<T>(options: {
  request: () => Promise<{ raw: Response }>;
  parse: (json: unknown) => T;
  fallback: string;
  ErrorClass?: typeof ApiRequestError;
}): Promise<T> {
  const ErrorClass = options.ErrorClass ?? ApiRequestError;

  try {
    const raw = await options.request();
    if (!raw.raw.ok) {
      throw await parseApiErrorResponse(raw.raw, options.fallback, ErrorClass);
    }

    const json = await raw.raw.json();
    return options.parse(json);
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }

    if (error instanceof ResponseError) {
      throw await parseApiErrorResponse(
        error.response,
        options.fallback,
        ErrorClass,
      );
    }

    throw error;
  }
}
